/**
 * Putting an event in the book by hand — decision 71, built after decision 64.
 *
 * Deliberately short. The record has thirty-odd fields and the harvest filled
 * most of them from a source; somebody typing an event they heard about at a
 * trade show knows its name, roughly when, and where. Everything else is edited
 * in the record afterwards, where it already can be.
 *
 * ## Why it was built after the columns changed, not before
 *
 * Decision 64 says `+ New event` comes after the table changes, "never onto the
 * current fifteen". A create form modelled on the harvest table would have
 * asked for working stage, date confidence, A·B·C axes and source — the fields
 * an importer fills, put to a person who has none of them. The form asks for
 * what an event *is*, which is what the table now shows.
 */
import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { EVENT_STAGES, type CrmEvent } from '@/types/events'
import '@/shared/ui/RecordForm.css'

/**
 * One form for creating an event and for correcting one — AVV-2026-001.
 *
 * Sales reported that once an event exists its own details cannot be changed.
 * That was true: the drawer edits the *workflow* around an event — stage, reach,
 * notes, who is going — and renders name, category, country, venue and dates as
 * a line of read-only text. Everything the create form asks for was write-once,
 * so a misspelt venue or a moved date meant deleting the event and losing its
 * assignments and its history with it.
 *
 * Rather than a second form, this one takes an optional `event`. Two forms for
 * one record is how the fields drift apart — the same reason `ShareholderModal`
 * handles a new holder and an existing one.
 */
export function NewEventModal({
  onClose,
  onCreate,
  event,
}: {
  onClose: () => void
  onCreate: (draft: Partial<CrmEvent>) => Promise<void>
  /** Absent when creating. Present, the form opens on it and saves a patch. */
  event?: CrmEvent | null
}) {
  const editing = !!event
  const [name, setName] = useState(event?.name ?? '')
  const [category, setCategory] = useState(event?.category ?? '')
  const [country, setCountry] = useState(event?.country ?? '')
  const [venue, setVenue] = useState(event?.venue ?? '')
  const [startDate, setStartDate] = useState(event?.startDate ?? '')
  const [endDate, setEndDate] = useState(event?.endDate ?? '')
  const [stage, setStage] = useState<CrmEvent['stage']>(event?.stage ?? 'unqualified')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    if (!name.trim()) {
      setError('An event needs a name — it is how the row is identified.')
      return
    }
    if (endDate && startDate && endDate < startDate) {
      setError('The event ends before it starts.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onCreate({
        name: name.trim(),
        category: category.trim(),
        country: country.trim(),
        venue: venue.trim(),
        startDate,
        // A one-day event has one date, and repeating it as the end reads as a
        // range somebody entered rather than a day.
        endDate: endDate || startDate,
        stage,
      })
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : editing
            ? 'Could not save the event.'
            : 'Could not create the event.',
      )
      setSaving(false)
    }
  }

  return (
    <Modal
      title={editing ? 'Edit event' : 'New event'}
      onClose={onClose}
      width={480}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? (editing ? 'Saving…' : 'Creating…') : editing ? 'Save changes' : 'Create event'}
          </button>
        </>
      }
    >
      <div className="recform">
        <section className="recform__group">
          <div className="recform__grid">
            <label className="field recform__wide">
              <span>Name</span>
              <input
                className="input"
                value={name}
                placeholder="e.g. Fjellsportfestivalen"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Type</span>
              <input
                className="input"
                value={category}
                placeholder="e.g. festival"
                onChange={(e) => setCategory(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Status</span>
              <select
                className="input"
                value={stage}
                onChange={(e) => setStage(e.target.value as CrmEvent['stage'])}
              >
                {EVENT_STAGES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Starts</span>
              <input
                className="input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Ends</span>
              <input
                className="input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Venue</span>
              <input
                className="input"
                value={venue}
                placeholder="e.g. Sogndal"
                onChange={(e) => setVenue(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Country</span>
              <input
                className="input"
                value={country}
                placeholder="e.g. Norway"
                onChange={(e) => setCountry(e.target.value)}
              />
            </label>
          </div>
          <p className="recform__note">
            Budget, registered and attended, the owner and who goes are set on the record. The
            score stays blank until relevance and reach are — the same state an unassessed import
            is in, rather than a nought that would sort it below events somebody judged and found
            weak.
          </p>
        </section>
        {error && <p className="recform__error">{error}</p>}
      </div>
    </Modal>
  )
}
